import { IoCreateOutline } from "react-icons/io5";
import IconButton from "../../../../components/Buttons/IconButton";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import { BsTrash3 } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import { AgGridReact } from "ag-grid-react";
import DefaultPagination from "../../../../components/Pagination";
import { get } from "../../../../utils/helpers/api";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../context/settings";
import AgGridTable from "../../../../components/Tables/AgGrid";

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setCategoryToEdit(data);
          context.setAddCategoryModalOpen(true);
        }}
        size="sm" outline color="secondary"
      >
        <IoCreateOutline />
      </IconButton>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setCategoryToDelete(data.id);
        }}
        size="sm" outline color="danger"
      >
        <BsTrash3 />
      </IconButton>
    </div>
  );
}

function AdjustmentsPage() {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const getStockAdjustments = useCallback(async () => {
    setLoading(true);
    const data = await get(`/inventory/stock-adjustments?page=${currentPage}&page_size=${pageSize}`);
    if(data?.success) {
      setRowData(data.stock_adjustments.data);
      setTotalPages(data.stock_adjustments.last_page);
      setTotalRecords(data.stock_adjustments.total);
    }
    setLoading(false);
  }, [currentPage, pageSize]);

  useEffect(() => {
    getStockAdjustments();
  }, [getStockAdjustments]);

  const columnDefs = useMemo(() => [
    { field: 'adjusted_at', headerName: t('common.date'), flex: 1, sortable: true, valueFormatter: (p) => formatDate(p.value) },
    { field: 'product.name', headerName: t('inventory.product'), flex: 1, sortable: true },
    { field: 'old_stock', headerName: t('inventory.oldStock'), flex: 1, sortable: true },
    { field: 'new_stock', headerName: t('inventory.newStock'), flex: 1, sortable: true },
    { field: 'difference', headerName: t('inventory.difference'), flex: 1, sortable: true },
    { field: 'reason', headerName: t('inventory.reason'), flex: 1, sortable: true },
    {
      field: 'created_by.firstname',
      headerName: t('common.createdBy'),
      flex: 1,
      sortable: true,
      valueFormatter: (params) => {
        const firstName = params.data.created_by?.firstname ?? '';
        const lastName = params.data.created_by?.lastname ?? '';
        return `${firstName} ${lastName}`;
      }
    }
  ], [t, formatDate]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const gridContext = useMemo(() => ({ navigate }), [navigate]);

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('inventory.adjustments')} onReload={getStockAdjustments} dataTotal={totalRecords}>
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/inventory/stock-adjustments/create')}>
            {t('inventory.newAdjustment')}
          </DefaultButton>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable 
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
          />

          <DefaultPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>
    </SidebarLayout>
  );
}

export default AdjustmentsPage;